// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { EducBox, ResultCard, InputRow, fmt } from './shared';

const calcRata = (importo, tanPerc, durMesi) => {
  const r = tanPerc / 100 / 12;
  if (r === 0) return importo / durMesi;
  return importo * (r * Math.pow(1 + r, durMesi)) / (Math.pow(1 + r, durMesi) - 1);
};

const calcTAEG = (importo, rata, durMesi, spese) => {
  // Deployment 17 — P0 Fix 2: TAEG corretto con metodo IRR (bisezione).
  // Risolve: capitale_netto = Σ_{t=1..N} rata / (1+r)^t  per r mensile.
  if (importo <= 0 || rata <= 0 || durMesi <= 0) return 0;
  const capitaleNetto = importo - (spese || 0);
  if (capitaleNetto <= 0) return 0;

  const f = (r) => {
    let pv = 0;
    for (let t = 1; t <= durMesi; t++) pv += rata / Math.pow(1 + r, t);
    return capitaleNetto - pv;
  };

  let a = 1e-6, b = 0.5; // r mensile tra 0% e 50%
  let fa = f(a), fb = f(b);
  // Espansione se necessario
  let tries = 0;
  while (fa * fb > 0 && b < 5 && tries < 30) {
    b *= 2; fb = f(b); tries++;
  }
  if (fa * fb > 0) return 0; // no root

  let mid = (a + b) / 2;
  for (let i = 0; i < 100; i++) {
    mid = (a + b) / 2;
    const fm = f(mid);
    if (Math.abs(fm) < 1e-9 || (b - a) / 2 < 1e-9) break;
    if (fm * fa > 0) { a = mid; fa = fm; } else { b = mid; fb = fm; }
  }
  // Tasso annuo effettivo
  return (Math.pow(1 + mid, 12) - 1) * 100;
};

const PianoDiAmmortamento = ({ importo, tan, durata, spese }) => {
  const rata = calcRata(importo, tan, durata);
  const r = (tan / 100) / 12;
  let saldo = importo;
  const rows = [];
  for (let i = 1; i <= durata; i++) {
    const interessi = saldo * r;
    const capitale = rata - interessi;
    saldo = Math.max(0, saldo - capitale);
    rows.push({ mese: i, rata, capitale, interessi, saldo });
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-white/[0.08]">
            {['Mese','Rata','Capitale','Interessi','Saldo residuo'].map(h => (
              <th key={h} className="py-2 px-2 text-[#888780] text-left font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.mese} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
              <td className="py-1.5 px-2 text-[#888780]">{row.mese}</td>
              <td className="py-1.5 px-2 text-[#E8E8E4]">€{fmt(row.rata)}</td>
              <td className="py-1.5 px-2 text-[#3D7A52]">€{fmt(row.capitale)}</td>
              <td className="py-1.5 px-2 text-[#C9A84C]">€{fmt(row.interessi)}</td>
              <td className="py-1.5 px-2 text-[#888780]">€{fmt(row.saldo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PrestitorPersonale = () => {
  const [importo, setImporto] = useState('10000');
  const [durata, setDurata] = useState('48');
  const [tan, setTan] = useState('7.5');
  const [istruttoria, setIstruttoria] = useState('200');
  const [showPiano, setShowPiano] = useState(false);

  const I = parseFloat(importo) || 0;
  const D = parseInt(durata) || 12;
  const T = parseFloat(tan) || 0;
  const S = parseFloat(istruttoria) || 0;

  const rata = useMemo(() => calcRata(I, T, D), [I, T, D]);
  const totalePagato = rata * D + S;
  const totaleInteressi = rata * D - I;
  const taeg = useMemo(() => calcTAEG(I, rata, D, S), [I, rata, D, S]);
  const taegColor = taeg < 5 ? '#3D7A52' : taeg < 10 ? '#C9A84C' : '#E84040';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="prestito-personale-calc">
      <EducBox text="Il prestito personale è un finanziamento non finalizzato: puoi usarlo per qualsiasi scopo. Il costo reale è il TAEG (Tasso Annuo Effettivo Globale) che include TAN + tutte le spese. Confronta sempre il TAEG, non solo la rata mensile." />

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <InputRow label="Importo richiesto (€)" value={importo} onChange={setImporto} prefix="€" />
        <InputRow label="Durata (mesi)" value={durata} onChange={setDurata} suffix="mesi" min={6} max={120} />
        <InputRow label="TAN % annuo" value={tan} onChange={setTan} suffix="%" tooltip="Tasso Annuo Nominale — solo gli interessi, senza spese accessorie" />
        <InputRow label="Spese istruttoria (€)" value={istruttoria} onChange={setIstruttoria} prefix="€" tooltip="Spese una-tantum al momento dell'erogazione" />
      </div>

      {/* TAN vs TAEG educational box */}
      <div className="mb-6 p-4 rounded-xl grid sm:grid-cols-2 gap-4" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
        <div>
          <p className="text-xs text-[#888780] mb-1">TAN (solo interessi)</p>
          <p className="text-2xl font-bold text-[#E8E8E4]">{T.toFixed(2)}%</p>
          <p className="text-xs text-[#888780] mt-1">Tasso nominale dichiarato dal creditore</p>
        </div>
        <div>
          <p className="text-xs text-[#888780] mb-1">TAEG (costo reale totale)</p>
          <p className="text-2xl font-bold" style={{ color: taegColor }}>{taeg.toFixed(2)}%</p>
          <p className="text-xs mt-1" style={{ color: taegColor }}>
            {taeg < 5 ? 'Tasso contenuto' : taeg < 10 ? 'Tasso nella media' : 'Tasso elevato — confronta altre offerte'}
          </p>
</div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <ResultCard label="Rata mensile" value={`€${fmt(rata)}`} gold />
        <ResultCard label="Totale interessi" value={`€${fmt(totaleInteressi)}`} />
        <ResultCard label="Totale pagato" value={`€${fmt(totalePagato)}`} />
        <ResultCard label="Costo extra vs cash" value={`€${fmt(totaleInteressi + S)}`} />
      </div>

      {/* Piano ammortamento collapsible */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          onClick={() => setShowPiano(!showPiano)}
          className="w-full flex items-center justify-between p-4 text-sm font-medium text-[#E8E8E4] hover:bg-white/[0.03] transition-colors"
          data-testid="piano-toggle"
        >
          <span>Piano di ammortamento ({D} rate)</span>
          <ChevronDown className={`w-4 h-4 text-[#888780] transition-transform ${showPiano ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence>
          {showPiano && (
            <motion.div
              initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
              className="overflow-hidden"
              style={{ borderTop: '1px solid rgba(255,255,255,0.06)', maxHeight: 400, overflowY: 'auto' }}
            >
              <div className="p-4">
                <PianoDiAmmortamento importo={I} tan={T} durata={D} spese={S} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <p className="text-xs text-[#888780] italic mt-4">Calcolo indicativo. Il TAEG reale dipende dal contratto specifico. Richiedi sempre il SECCI (documento europeo standardizzato sul credito ai consumatori) prima di firmare.</p>
</motion.div>
  );
};

export default PrestitorPersonale;
